// =============================================================================
// FILE: packages/renderer/src/components/client/ClientPortal.tsx
// PURPOSE: Invite-only VIP Client Portal. Managed CAD/BIM clients sign in to a
//   tenant-isolated workspace, see their $18k budget/phase status, pay phase
//   milestones (nothing advances without pay), track markups/prints and their
//   full ledger. Freely enter/leave via the landing-page entry.
// =============================================================================

import { useState, useEffect, useCallback, type JSX } from 'react';
import {
  Crown, Lock, LogOut, Loader2, Check, CreditCard, ReceiptText, Layers,
  ShieldCheck, X, ArrowRight, Sparkles, FileText, Download,
} from 'lucide-react';
import {
  clientToken, clientLogin, clientRegister, clientWorkspace, payNextPhase,
  payAdvance, checkoutStatus, clientInvoices, openClientPdf,
  type ClientWorkspace, type ClientEngagement, type ClientInvoice,
} from '../../lib/clientPortal.js';

const money = (n: number): string => `$${Math.round(n).toLocaleString()}`;
const field = { background: 'rgba(2,6,12,0.7)', border: '1px solid rgba(212,175,55,0.3)' } as const;

export function ClientPortal({ onExit }: { onExit: () => void }): JSX.Element {
  const [authed, setAuthed] = useState<boolean>(!!clientToken.get());
  const [mode, setMode] = useState<'signin' | 'activate'>('signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [clientName, setClientName] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [ws, setWs] = useState<ClientWorkspace | null>(null);
  const [invoices, setInvoices] = useState<ClientInvoice[]>([]);
  const [payMsg, setPayMsg] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      setWs(await clientWorkspace());
      setInvoices(await clientInvoices());
    }
    catch (e) { if ((e as Error).message.includes('session')) { clientToken.clear(); setAuthed(false); } }
  }, []);

  useEffect(() => { if (authed) void refresh(); }, [authed, refresh]);

  // Stripe return → poll status then refresh workspace.
  useEffect(() => {
    if (!authed) return;
    const sid = new URLSearchParams(window.location.search).get('managed_session');
    if (!sid) return;
    let tries = 0;
    const poll = async (): Promise<void> => {
      tries += 1;
      try {
        const s = await checkoutStatus(sid);
        if (s.payment_status === 'paid') { setPayMsg('Payment received — your project has advanced.'); void refresh(); window.history.replaceState({}, '', window.location.pathname); return; }
        if (s.status === 'expired' || tries > 6) { setPayMsg('Payment not completed.'); window.history.replaceState({}, '', window.location.pathname); return; }
      } catch { /* retry */ }
      window.setTimeout(() => void poll(), 1800);
    };
    void poll();
  }, [authed, refresh]);

  const submit = async (): Promise<void> => {
    setBusy(true); setErr(null);
    try {
      const r = mode === 'signin'
        ? await clientLogin(email.trim(), password)
        : await clientRegister(email.trim(), password, clientName.trim());
      clientToken.set(r.token, r.email);
      setAuthed(true);
    } catch (e) { setErr((e as Error).message); }
    setBusy(false);
  };

  const doPayAdvance = async (eng: ClientEngagement): Promise<void> => {
    setBusy(true); setPayMsg(null);
    try {
      const r = await payAdvance(eng.id);
      if (r.mode === 'stripe' && r.url) { window.location.href = r.url; return; }
      setPayMsg(r.message ?? `Send ${money(r.amount)} to ${r.payoutPaypal} via PayPal.`);
    } catch (e) { setPayMsg((e as Error).message); }
    setBusy(false);
  };

  const doPayPhase = async (eng: ClientEngagement): Promise<void> => {
    setBusy(true); setPayMsg(null);
    try {
      const r = await payNextPhase(eng.id);
      if (r.mode === 'stripe' && r.url) { window.location.href = r.url; return; }
      setPayMsg(r.message ?? `Send ${money(r.amount)} to ${r.payoutPaypal} via PayPal for the ${r.phasePct}% phase.`);
    } catch (e) { setPayMsg((e as Error).message); }
    setBusy(false);
  };

  const logout = (): void => { clientToken.clear(); setAuthed(false); setWs(null); setEmail(''); setPassword(''); };

  const viewAgreement = async (eng: ClientEngagement): Promise<void> => {
    try { await openClientPdf(`/api/managed/agreement/${eng.id}.pdf`); }
    catch (e) { setPayMsg((e as Error).message); }
  };
  const viewInvoice = async (inv: ClientInvoice): Promise<void> => {
    try { await openClientPdf(`/api/managed/invoice/${inv.id}.pdf`); }
    catch (e) { setPayMsg((e as Error).message); }
  };

  return (
    <div data-testid="client-portal" className="fixed inset-0 z-[70] overflow-y-auto" style={{ background: 'radial-gradient(1200px 800px at 70% -10%, rgba(212,175,55,0.10), transparent), #06070c' }}>
      {/* Header bar */}
      <div className="sticky top-0 z-10 flex items-center justify-between px-6 py-4 border-b" style={{ borderColor: 'rgba(212,175,55,0.2)', background: 'rgba(6,8,14,0.85)', backdropFilter: 'blur(14px)' }}>
        <div className="flex items-center gap-2.5">
          <div className="h-8 w-8 rounded-md grid place-items-center" style={{ background: 'linear-gradient(135deg,#f4d576,#b8860b)' }}><Crown className="h-4.5 w-4.5 text-zinc-900" strokeWidth={2} /></div>
          <div className="leading-tight">
            <div className="text-[13px] font-bold tracking-wide text-amber-100">RCD² Client Portal</div>
            <div className="text-[9.5px] uppercase tracking-[0.24em] text-amber-500/70">VIP Managed Workspace</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {authed && <button data-testid="client-portal-logout" onClick={logout} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11.5px] font-semibold text-zinc-300 hover:text-white transition" style={{ border: '1px solid rgba(148,163,184,0.25)' }}><LogOut className="h-3.5 w-3.5" /> Sign out</button>}
          <button data-testid="client-portal-exit" onClick={onExit} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11.5px] font-semibold text-zinc-300 hover:text-white transition" style={{ border: '1px solid rgba(148,163,184,0.25)' }}><X className="h-3.5 w-3.5" /> Exit Portal</button>
        </div>
      </div>

      {!authed ? (
        <div className="max-w-md mx-auto px-6 py-14">
          <div className="text-center mb-7">
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] uppercase tracking-[0.2em] mb-4" style={{ background: 'rgba(212,175,55,0.1)', border: '1px solid rgba(212,175,55,0.3)', color: '#e7c66b' }}><Sparkles className="h-3 w-3" /> Invitation Only</div>
            <h2 className="text-2xl font-bold text-amber-50">Welcome to your VIP workspace</h2>
            <p className="text-[13px] text-zinc-400 mt-2">Sign in to manage your RCD² CAD/BIM projects, budgets and deliverables.</p>
          </div>

          <div className="flex gap-1 p-1 rounded-xl mb-5" style={{ background: 'rgba(2,6,12,0.7)', border: '1px solid rgba(148,163,184,0.15)' }}>
            {(['signin', 'activate'] as const).map((m) => (
              <button key={m} data-testid={`client-tab-${m}`} onClick={() => { setMode(m); setErr(null); }} className="flex-1 px-3 py-2 rounded-lg text-[12px] font-semibold transition" style={{ background: mode === m ? 'rgba(212,175,55,0.18)' : 'transparent', color: mode === m ? '#f4d576' : '#94a3b8' }}>{m === 'signin' ? 'Sign In' : 'Activate Invite'}</button>
            ))}
          </div>

          <div className="panel p-6 space-y-3.5" style={{ border: '1px solid rgba(212,175,55,0.22)' }}>
            {mode === 'activate' && (
              <label className="text-[10px] uppercase tracking-wide text-zinc-500 block">Company / Your Name
                <input data-testid="client-name-input" value={clientName} onChange={(e) => setClientName(e.target.value)} className="mt-1 w-full px-3 py-2 rounded-lg text-[13px] text-zinc-100 outline-none" style={field} />
              </label>
            )}
            <label className="text-[10px] uppercase tracking-wide text-zinc-500 block">Invited Email
              <input data-testid="client-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" className="mt-1 w-full px-3 py-2 rounded-lg text-[13px] text-zinc-100 outline-none" style={field} />
            </label>
            <label className="text-[10px] uppercase tracking-wide text-zinc-500 block">Password{mode === 'activate' ? ' (choose one, min 6 chars)' : ''}
              <input data-testid="client-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') void submit(); }} className="mt-1 w-full px-3 py-2 rounded-lg text-[13px] text-zinc-100 outline-none" style={field} />
            </label>
            {err && <div data-testid="client-auth-error" className="px-3 py-2 rounded-lg text-[12px]" style={{ background: 'rgba(244,63,94,0.1)', border: '1px solid rgba(244,63,94,0.3)', color: '#fda4af' }}>{err}</div>}
            <button data-testid="client-auth-submit" onClick={submit} disabled={busy || !email || password.length < (mode === 'activate' ? 6 : 1)} className="w-full inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg text-[13px] font-bold disabled:opacity-50" style={{ background: 'linear-gradient(135deg,#f4d576,#b8860b)', color: '#1a1204' }}>
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : mode === 'signin' ? <Lock className="h-4 w-4" /> : <ArrowRight className="h-4 w-4" />}
              {mode === 'signin' ? 'Enter Workspace' : 'Activate & Enter'}
            </button>
            <p className="text-[10.5px] text-zinc-500 text-center leading-relaxed">Access is granted by RCD² invitation only. Don't have an invite? <a href="mailto:rcd2engine@gmail.com?subject=RCD2%20Client%20Portal%20Access" className="text-amber-400/80 hover:text-amber-300">Request access →</a></p>
          </div>
        </div>
      ) : (
        <div className="max-w-[1100px] mx-auto px-6 py-10">
          {payMsg && <div data-testid="client-pay-msg" className="mb-5 px-4 py-2.5 rounded-lg text-[12.5px]" style={{ background: 'rgba(52,211,153,0.1)', border: '1px solid rgba(52,211,153,0.3)', color: '#a7f3d0' }}>{payMsg}</div>}
          <div className="mb-6">
            <div className="text-[10px] uppercase tracking-[0.22em] text-amber-500/80 mb-1">Signed in as {ws?.email}</div>
            <h2 className="text-2xl font-bold text-amber-50">Your Managed Projects</h2>
          </div>

          {!ws || ws.engagements.length === 0 ? (
            <div data-testid="client-empty" className="panel p-8 text-center" style={{ border: '1px solid rgba(212,175,55,0.2)' }}>
              <ShieldCheck className="h-9 w-9 mx-auto text-amber-400/60 mb-3" />
              <div className="text-[14px] font-semibold text-zinc-200">No managed projects yet</div>
              <p className="text-[12.5px] text-zinc-400 mt-2 max-w-md mx-auto">Your RCD² coordinator will set up your first managed engagement. Once created, it appears here and you can pay the advance to activate it.</p>
            </div>
          ) : (
            <div className="space-y-6">
              {ws.engagements.map((eng) => (
                <div key={eng.id} data-testid={`client-engagement-${eng.id}`} className="panel p-6" style={{ border: '1px solid rgba(212,175,55,0.2)' }}>
                  <div className="flex flex-wrap items-start justify-between gap-4">
                    <div>
                      <div className="text-[11px] uppercase tracking-wide text-zinc-500">{eng.clientName}</div>
                      <div className="text-xl font-bold text-zinc-100">{eng.projectName}</div>
                      <div className="mt-1 inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wide" data-testid={`client-status-${eng.id}`} style={{ background: eng.status === 'active' ? 'rgba(52,211,153,0.16)' : eng.status === 'completed' ? 'rgba(56,189,248,0.16)' : 'rgba(251,146,60,0.16)', color: eng.status === 'active' ? '#6ee7b7' : eng.status === 'completed' ? '#7dd3fc' : '#fdba74' }}>{eng.status.replace('_', ' ')}</div>
                    </div>
                    <div className="flex gap-2 flex-wrap">
                      <button data-testid={`client-agreement-${eng.id}`} onClick={() => viewAgreement(eng)} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-[12px] font-semibold" style={{ background: 'rgba(167,139,250,0.14)', border: '1px solid rgba(167,139,250,0.4)', color: '#c4b5fd' }}><FileText className="h-3.5 w-3.5" /> Agreement PDF</button>
                      {eng.status === 'awaiting_payment' && (
                        <button data-testid={`client-pay-advance-${eng.id}`} onClick={() => doPayAdvance(eng)} disabled={busy} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-[12px] font-bold disabled:opacity-60" style={{ background: 'linear-gradient(135deg,#2dd4bf,#0d9488)', color: '#04121b' }}>{busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CreditCard className="h-3.5 w-3.5" />} Pay Advance {money(eng.advance)}</button>
                      )}
                      {eng.status === 'active' && eng.nextPhase && (
                        <button data-testid={`client-pay-phase-${eng.id}`} onClick={() => doPayPhase(eng)} disabled={busy} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-[12px] font-bold disabled:opacity-60" style={{ background: 'linear-gradient(135deg,#f4d576,#b8860b)', color: '#1a1204' }}>{busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CreditCard className="h-3.5 w-3.5" />} Pay {eng.nextPhase.pct}% Phase · {money(eng.nextPhase.amount)}</button>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5 mt-5">
                    {([['Budget', money(eng.budget), '#22d3ee'], ['Paid Milestone', `${eng.paidPct}%`, '#34d399'], ['Markups', String(eng.markups), '#7dd3fc'], ['PDF Prints', String(eng.prints), '#a78bfa']] as [string, string, string][]).map(([l, v, c]) => (
                      <div key={l} data-testid={`client-kpi-${l.toLowerCase().replace(/ /g, '-')}`} className="p-3 rounded-lg" style={{ background: 'rgba(6,12,22,0.7)', border: '1px solid rgba(148,163,184,0.16)' }}><div className="text-[9.5px] uppercase tracking-wide text-zinc-500 mb-1">{l}</div><div className="text-[15px] font-bold" style={{ color: c }}>{v}</div></div>
                    ))}
                  </div>

                  {/* Phase progression — nothing advances without pay */}
                  <div className="mt-5">
                    <div className="text-[11px] uppercase tracking-[0.16em] text-zinc-500 mb-3 flex items-center gap-1.5"><Layers className="h-3.5 w-3.5 text-amber-400" /> Phase Milestones</div>
                    <div className="flex items-center gap-1">
                      {eng.phases.map((p, i) => (
                        <div key={p.pct} className="flex items-center gap-1 flex-1">
                          <div className="flex-1 text-center">
                            <div className="h-9 w-9 mx-auto grid place-items-center rounded-full text-[12px] font-bold" data-testid={`client-phase-${eng.id}-${p.pct}`} style={{ background: p.billed ? 'rgba(52,211,153,0.2)' : 'rgba(15,23,42,0.7)', border: `1px solid ${p.billed ? 'rgba(52,211,153,0.6)' : 'rgba(212,175,55,0.3)'}`, color: p.billed ? '#6ee7b7' : '#e7c66b' }}>{p.billed ? <Check className="h-4 w-4" /> : `${p.pct}`}</div>
                            <div className="text-[9.5px] text-zinc-500 mt-1">{p.pct}%</div>
                          </div>
                          {i < eng.phases.length - 1 && <div className="h-px flex-1" style={{ background: p.billed ? 'rgba(52,211,153,0.4)' : 'rgba(148,163,184,0.2)' }} />}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ))}

              {/* Invoices */}
              {invoices.length > 0 && (
                <div className="panel p-6" data-testid="client-invoices" style={{ border: '1px solid rgba(212,175,55,0.2)' }}>
                  <div className="text-[12px] uppercase tracking-[0.16em] text-zinc-400 mb-3 flex items-center gap-2"><FileText className="h-4 w-4 text-amber-400" /> Invoices</div>
                  <div className="space-y-2">
                    {invoices.map((inv) => (
                      <div key={inv.id} data-testid={`client-invoice-${inv.id}`} className="flex flex-wrap items-center justify-between gap-2 py-2 px-3 rounded-lg" style={{ background: 'rgba(6,12,22,0.6)', border: '1px solid rgba(148,163,184,0.14)' }}>
                        <div>
                          <div className="text-[12.5px] font-semibold text-zinc-100">{inv.invoiceNo} · {inv.phasePct}% phase</div>
                          <div className="text-[10.5px] text-zinc-500">{inv.projectName} · due {inv.dueDate} · {money(inv.total)}</div>
                        </div>
                        <div className="flex gap-2">
                          <button data-testid={`invoice-pdf-${inv.id}`} onClick={() => viewInvoice(inv)} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11.5px] font-semibold" style={{ background: 'rgba(148,163,184,0.12)', border: '1px solid rgba(148,163,184,0.3)', color: '#cbd5e1' }}><Download className="h-3.5 w-3.5" /> PDF</button>
                          {inv.payUrl && <button data-testid={`invoice-pay-${inv.id}`} onClick={() => { window.location.href = inv.payUrl; }} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11.5px] font-bold" style={{ background: 'linear-gradient(135deg,#2dd4bf,#0d9488)', color: '#04121b' }}><CreditCard className="h-3.5 w-3.5" /> Pay Now</button>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Ledger */}
              <div className="panel p-6" data-testid="client-ledger" style={{ border: '1px solid rgba(212,175,55,0.2)' }}>
                <div className="text-[12px] uppercase tracking-[0.16em] text-zinc-400 mb-3 flex items-center gap-2"><ReceiptText className="h-4 w-4 text-amber-400" /> Your Activity Ledger</div>
                {ws.ledger.length === 0 ? <div className="text-[12px] text-zinc-500">No activity yet.</div> : (
                  <div className="space-y-1.5 max-h-64 overflow-y-auto">
                    {ws.ledger.map((l, i) => (
                      <div key={i} className="flex items-center justify-between text-[12px] py-1.5 px-2 rounded-md" style={{ background: 'rgba(6,12,22,0.5)' }}>
                        <span className="text-zinc-300">{l.kind.replace(/_/g, ' ')}{l.method ? ` · ${l.method}` : ''}{l.phasePct ? ` · ${l.phasePct}%` : ''}</span>
                        <span className="text-zinc-500">{l.amount ? money(l.amount) : ''} · {new Date(l.at).toLocaleDateString()}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
